// frontend/src/pages/Facilitation/FacilitationList.jsx

// ✅ CON FILTRO POR FACILITADOR Y SECCIONES PRÓXIMAS/IMPARTIDAS

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  Table,
  Badge,
  Input,
  InputGroup,
  InputGroupText,
  Nav,
  NavItem,
  NavLink,
} from 'reactstrap';
import { ordersAPI, facilitatorsAPI, quotesAPI } from '../../services/api';
import { parseDate } from '../../utils/dates';

function FacilitationList() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [facilitators, setFacilitators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFacilitator, setSelectedFacilitator] = useState('all');
  const [activeTab, setActiveTab] = useState('upcoming'); // 'upcoming' o 'completed'
  const [pendingQuotes, setPendingQuotes] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Cargar órdenes
      const ordersResponse = await ordersAPI.getAll();
      const ordersData = ordersResponse.data.results || ordersResponse.data;
      
      // Cargar facilitadores
      const facilitatorsResponse = await facilitatorsAPI.getAll();
      const facilitatorsData = facilitatorsResponse.data.results || facilitatorsResponse.data;
      
            setOrders(Array.isArray(ordersData) ? ordersData : []);
      setFacilitators(Array.isArray(facilitatorsData) ? facilitatorsData : []);

      // Cargar cotizaciones aceptadas sin OC
      try {
        const pqResponse = await quotesAPI.getPendingFacilitation();
        if (pqResponse.data && pqResponse.data.success) {
          setPendingQuotes(Array.isArray(pqResponse.data.quotes) ? pqResponse.data.quotes : []);
        }
      } catch (e) {
        console.warn('Error cargando quotes pendientes de facilitación:', e);
        setPendingQuotes([]);
      }
    } catch (error) {
      console.error('Error al cargar datos:', error);
      setOrders([]);
      setFacilitators([]);
    } finally {
      setLoading(false);
    }
  };

    // ✅ Normalizar quotes a formato de orden para mostrarlas en la tabla
  const normalizeQuoteToOrder = (quote) => {
    const fac = quote._facilitation || quote.manual_acceptance_data || {};
    return {
      id: quote.id,
      order_number: quote.proposal_number || 'Sin número',
      billing_name: quote.client_name || '',
      facilitator_name: fac.facilitator_name || '',
      facilitator_id: fac.facilitator_id,
      invoice_schedule_details: {
        service_delivery_date: fac.service_delivery_date || null,
      },
      _type: 'quote',
      _quote: quote,
    };
  };

  // Expande una orden/quote en filas por sesión (una fila por sesión de facilitación)
  const expandToSessionRows = (item) => {
    const sessions = item.invoice_schedule_details?.facilitation_sessions;
    if (sessions && sessions.length > 0) {
      return sessions.map(session => ({
        ...item,
        _session_date: session.date,
        _session_facilitator_name: session.facilitator_name || '',
        _session_facilitator_id: session.facilitator_id,
        _session_notes: session.notes,
        _session_id: session.id,
      }));
    }
    // Fallback: sin sesiones nuevas, usar campos legados
    return [{
      ...item,
      _session_date: item.invoice_schedule_details?.service_delivery_date || null,
      _session_facilitator_name: item.invoice_schedule_details?.facilitator_name || item.facilitator_name || '',
      _session_facilitator_id: item.facilitator_id || null,
      _session_notes: '',
      _session_id: null,
    }];
  };

  // ✅ Filtrar y ordenar órdenes
  const getFilteredOrders = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Merge orders + quotes pendientes, luego expandir por sesiones
    const allItems = [
      ...orders.map(o => ({ ...o, _type: 'order' })),
      ...pendingQuotes.map(normalizeQuoteToOrder),
    ];

    const expandedRows = allItems.flatMap(expandToSessionRows);

    return expandedRows
      .filter((row) => {
        const searchLower = searchTerm.toLowerCase();
        const matchesSearch =
          (row.order_number && row.order_number.toLowerCase().includes(searchLower)) ||
          (row.billing_name && row.billing_name.toLowerCase().includes(searchLower)) ||
          (row._session_facilitator_name && row._session_facilitator_name.toLowerCase().includes(searchLower));

        if (!matchesSearch) return false;

        // Filtro por facilitador
        if (selectedFacilitator !== 'all') {
          if (selectedFacilitator === 'unassigned') {
            if (row._session_facilitator_name) return false;
          } else {
            if (row._session_facilitator_name !== selectedFacilitator) return false;
          }
        }

        const serviceDate = row._session_date;
        if (!serviceDate) return activeTab === 'upcoming';

        const serviceDateObj = new Date(serviceDate);
        serviceDateObj.setHours(0, 0, 0, 0);

        return activeTab === 'upcoming' ? serviceDateObj >= today : serviceDateObj < today;
      })
      .sort((a, b) => {
        const dateA = a._session_date;
        const dateB = b._session_date;

        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;

        const dateObjA = new Date(dateA);
        const dateObjB = new Date(dateB);

        return activeTab === 'upcoming' ? dateObjA - dateObjB : dateObjB - dateObjA;
      });
  };


  const filteredOrders = getFilteredOrders();

    // ✅ Estadísticas (incluyendo quotes pendientes)
  const upcomingCount = [...orders, ...pendingQuotes.map(normalizeQuoteToOrder)].filter((o) => {
    const invoiceSchedule = o.invoice_schedule_details;
    const serviceDate = invoiceSchedule?.service_delivery_date;
    
    if (!serviceDate) return true;
    
    const serviceDateObj = new Date(serviceDate);
    serviceDateObj.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return serviceDateObj >= today;
  }).length;

  const completedCount = [...orders, ...pendingQuotes.map(normalizeQuoteToOrder)].filter((o) => {
    const invoiceSchedule = o.invoice_schedule_details;
    const serviceDate = invoiceSchedule?.service_delivery_date;
    
    if (!serviceDate) return false;
    
    const serviceDateObj = new Date(serviceDate);
    serviceDateObj.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    return serviceDateObj < today;
  }).length;

  const pendingOCCount = pendingQuotes.length;



  if (loading) {
    return (
      <>
        <div className="min-height-300 bg-gradient-primary position-absolute w-100"></div>
        <Container fluid className="py-4">
          <div className="text-center mt-5">
            <div className="spinner-border text-white" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
            <p className="mt-3 text-white">Cargando servicios...</p>
          </div>
        </Container>
      </>
    );
  }

  return (
    <>
      <div className="min-height-300 bg-gradient-primary position-absolute w-100"></div>

      <Container fluid className="py-4">
        {/* ✅ Stats Cards */}
        <Row>
          <Col xl="4" sm="6" className="mb-xl-0 mb-4">
            <Card className="shadow border-0">
              <CardBody className="p-3">
                <Row>
                  <div className="col-8">
                    <div className="numbers">
                      <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">
                        Total Servicios
                      </p>
                      <h5 className="font-weight-bolder mb-0 text-dark">{orders.length + pendingQuotes.length}</h5>
                    </div>
                  </div>
                  <div className="col-4 text-end">
                    <div className="icon icon-shape bg-gradient-primary shadow-primary text-center rounded-circle">
                      <i className="ni ni-hat-3 text-lg opacity-10 text-white"></i>
                    </div>
                  </div>
                </Row>
              </CardBody>
            </Card>
          </Col>

          <Col xl="4" sm="6" className="mb-xl-0 mb-4">
            <Card className="shadow border-0">
              <CardBody className="p-3">
                <Row>
                  <div className="col-8">
                    <div className="numbers">
                      <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">
                        Próximas
                      </p>
                      <h5 className="font-weight-bolder mb-0 text-dark">{upcomingCount}</h5>
                    </div>
                  </div>
                  <div className="col-4 text-end">
                    <div className="icon icon-shape bg-gradient-success shadow-success text-center rounded-circle">
                      <i className="ni ni-calendar-grid-58 text-lg opacity-10 text-white"></i>
                    </div>
                  </div>
                </Row>
              </CardBody>
            </Card>
          </Col>

          <Col xl="4" sm="6">
            <Card className="shadow border-0">
              <CardBody className="p-3">
                <Row>
                  <div className="col-8">
                    <div className="numbers">
                      <p className="text-sm mb-0 text-uppercase font-weight-bold text-muted">
                        Impartidas
                      </p>
                      <h5 className="font-weight-bolder mb-0 text-dark">{completedCount}</h5>
                    </div>
                  </div>
                  <div className="col-4 text-end">
                    <div className="icon icon-shape bg-gradient-info shadow-info text-center rounded-circle">
                      <i className="ni ni-check-bold text-lg opacity-10 text-white"></i>
                    </div>
                  </div>
                </Row>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ✅ Tabla Principal */}
        <Row className="mt-4">
          <Col lg="12">
            <Card className="shadow border-0 mb-4">
              <CardHeader className="bg-gradient-primary pb-3">
                <Row className="align-items-center">
                  <Col md="6">
                    <h6 className="mb-0 text-white">
                      <i className="ni ni-hat-3 me-2"></i>
                      Facilitación
                    </h6>
                  </Col>
                </Row>

                {/* ✅ Filtros */}
                <Row className="mt-3">
                  <Col md="6">
                    <InputGroup className="input-group-alternative shadow-sm">
                      <InputGroupText className="bg-white">
                        <i className="fas fa-search text-muted"></i>
                      </InputGroupText>
                      <Input
                        type="text"
                        className="form-control-alternative"
                        placeholder="Buscar por orden, cliente o facilitador..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </InputGroup>
                  </Col>
                  <Col md="6">
                    <Input
                      type="select"
                      className="form-control-alternative shadow-sm"
                      value={selectedFacilitator}
                      onChange={(e) => setSelectedFacilitator(e.target.value)}
                    >
                      <option value="all">Todos los facilitadores</option>
                      <option value="unassigned">Sin asignar</option>
                      {facilitators.map((f) => (
                        <option key={f.id} value={f.name}>
                          {f.name}
                        </option>
                      ))}
                    </Input>
                  </Col>
                </Row>

                {/* ✅ Tabs: Próximas / Impartidas */}
                <Nav tabs className="mt-3">
                  <NavItem>
                    <NavLink
                      className={activeTab === 'upcoming' ? 'active bg-white text-primary' : 'text-white'}
                      onClick={() => setActiveTab('upcoming')}
                      style={{ cursor: 'pointer', fontWeight: 'bold' }}
                    >
                      <i className="ni ni-calendar-grid-58 me-1"></i>
                      Próximas ({upcomingCount})
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={activeTab === 'completed' ? 'active bg-white text-primary' : 'text-white'}
                      onClick={() => setActiveTab('completed')}
                      style={{ cursor: 'pointer', fontWeight: 'bold' }}
                    >
                      <i className="ni ni-check-bold me-1"></i>
                      Impartidas ({completedCount})
                    </NavLink>
                  </NavItem>
                </Nav>
              </CardHeader>

              <CardBody className="px-0 pt-0 pb-2">
                <div className="table-responsive p-0">
                  <Table className="align-items-center mb-0" hover>
                    <thead>
                      <tr>
                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                          # Orden
                        </th>
                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                          Cliente
                        </th>
                        <th className="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">
                          Facilitador
                        </th>
                        <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                          Fecha Impartición
                        </th>
                        <th className="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">
                          Estado
                        </th>
                        <th className="text-secondary opacity-7"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOrders.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="text-center py-5">
                            <i className="ni ni-hat-3 text-muted" style={{ fontSize: '3rem' }}></i>
                            <p className="text-xs text-secondary mb-0 mt-3">
                              {searchTerm || selectedFacilitator !== 'all'
                                ? 'No se encontraron servicios con ese criterio'
                                : `No hay servicios ${activeTab === 'upcoming' ? 'próximos' : 'impartidos'}`}
                            </p>
                          </td>
                        </tr>
                      ) : (
                        filteredOrders.map((order, rowIdx) => {
                          const serviceDate = order._session_date;
                          const facilitatorName = order._session_facilitator_name;

                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          let dateStatus = 'pending';
                          let daysInfo = '';
                          if (serviceDate) {
                            const serviceDateObj = new Date(serviceDate);
                            serviceDateObj.setHours(0, 0, 0, 0);
                            const diffDays = Math.ceil((serviceDateObj - today) / (1000 * 60 * 60 * 24));
                            if (diffDays < 0) { dateStatus = 'completed'; daysInfo = `Hace ${Math.abs(diffDays)} días`; }
                            else if (diffDays === 0) { dateStatus = 'today'; daysInfo = 'Hoy'; }
                            else if (diffDays <= 7) { dateStatus = 'soon'; daysInfo = `En ${diffDays} días`; }
                            else { dateStatus = 'upcoming'; daysInfo = `En ${diffDays} días`; }
                          }

                        return (
                          <tr
                            key={`${order.id}-${order._session_id ?? rowIdx}`}
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              if (order._type === 'quote') {
                                navigate(`/admin/facilitation/pending/${order.id}`);
                              } else {
                                navigate(`/admin/facilitation/${order.id}`);
                              }
                            }}
                          >
                              <td>
                                <div className="d-flex px-2 py-1">
                                  <div>
                                    <div className="icon icon-shape icon-sm bg-gradient-primary shadow text-center rounded-circle me-2">
                                      <i className="ni ni-box-2 text-white text-xs"></i>
                                    </div>
                                  </div>
                                  <div className="d-flex flex-column justify-content-center">
                                    <h6 className="mb-0 text-sm text-dark">{order.order_number}</h6>
                                  </div>
                                </div>
                              </td>
                              <td>
                                <p className="text-sm font-weight-bold mb-0 text-dark">
                                  {order.billing_name || order.quote_client_name || 'N/A'}
                                </p>
                              </td>
                              <td>
                                {facilitatorName ? (
                                  <Badge color="info" className="badge-sm">
                                    <i className="ni ni-single-02 me-1"></i>
                                    {facilitatorName}
                                  </Badge>
                                ) : (
                                  <Badge color="primary" className="badge-sm">
                                    Sin asignar
                                  </Badge>
                                )}
                              </td>
                              <td className="align-middle text-center">
                                {serviceDate ? (
                                  <div>
                                    <p className="text-sm font-weight-bold mb-0 text-dark">
                                      {parseDate(serviceDate).toLocaleDateString('es-MX', {
                                        weekday: 'short',
                                        year: 'numeric',
                                        month: 'short',
                                        day: 'numeric',
                                      })}
                                    </p>
                                    <p className="text-xs text-secondary mb-0">{daysInfo}</p>
                                  </div>
                                ) : (
                                  <Badge color="warning" className="badge-sm">
                                    Sin programar
                                  </Badge>
                                )}
                              </td>
                              <td className="align-middle text-center">
                                {order._type === 'quote' ? (
                                  <Badge color="warning" className="badge-sm">
                                    <i className="ni ni-time-alarm me-1"></i>
                                    Pendiente OC
                                  </Badge>
                                ) : (
                                  <>
                                    {dateStatus === 'completed' && (
                                      <Badge color="success" className="badge-sm">
                                        <i className="ni ni-check-bold me-1"></i>
                                        Impartida
                                      </Badge>
                                    )}
                                    {dateStatus === 'today' && (
                                      <Badge color="danger" className="badge-sm">
                                        <i className="ni ni-time-alarm me-1"></i>
                                        Hoy
                                      </Badge>
                                    )}
                                    {dateStatus === 'soon' && (
                                      <Badge color="warning" className="badge-sm">
                                        <i className="ni ni-calendar-grid-58 me-1"></i>
                                        Próxima
                                      </Badge>
                                    )}
                                    {dateStatus === 'upcoming' && (
                                      <Badge color="info" className="badge-sm">
                                        <i className="ni ni-calendar-grid-58 me-1"></i>
                                        Programada
                                      </Badge>
                                    )}
                                    {dateStatus === 'pending' && (
                                      <Badge color="primary" className="badge-sm">
                                        Pendiente
                                      </Badge>
                                    )}
                                  </>
                                )}
                              </td>
                              <td className="align-middle text-center">
                                <Button
                                  color="primary"
                                  size="sm"
                                  className="btn-icon shadow-sm"
                                                                    onClick={(e) => {
                                    e.stopPropagation();
                                    if (order._type === 'quote') {
                                      navigate(`/admin/facilitation/pending/${order.id}`);
                                    } else {
                                      navigate(`/admin/facilitation/${order.id}`);
                                    }
                                  }}
                                >
                                  <span className="btn-inner--text">Ver Detalle</span>
                                </Button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </Table>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default FacilitationList;
